/**
 * Activation de la double authentification depuis l'APP (07/10/2026).
 *
 * Elle est obligatoire pour un proprietaire ou un administrateur de compte
 * (FON-02), mais l'APP n'avait AUCUN ecran pour l'activer : elle renvoyait
 * vers « l'administrateur de votre organisation ». Un client qui cree son
 * compte en est le proprietaire : sans cet ecran, il ne pouvait jamais se
 * connecter. Meme parcours que l'Admin (screens/Login.tsx de l'Admin).
 */
import { useEffect, useRef, useState } from "react";
import QRCode from "qrcode";

import { api } from "../api";
import { Erreur } from "../components/ui";

export function ActivationMfa({ email, motDePasse, onActivee, onAnnuler }: {
  email: string; motDePasse: string; onActivee: () => void; onAnnuler: () => void;
}) {
  const [uri, setUri] = useState<string | null>(null);
  const [code, setCode] = useState("");
  const [envoi, setEnvoi] = useState(false);
  const [erreur, setErreur] = useState<unknown>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    api.mfaSetup(email, motDePasse).then((r) => setUri(r.provisioning_uri), setErreur);
  }, [email, motDePasse]);

  // Le QR est genere LOCALEMENT : l'URI contient le secret, il ne doit
  // passer par aucun service tiers.
  useEffect(() => {
    if (uri && canvasRef.current) void QRCode.toCanvas(canvasRef.current, uri, { width: 220, margin: 1 });
  }, [uri]);

  const secret = uri ? new URLSearchParams(uri.split("?")[1] ?? "").get("secret") ?? "" : "";

  return (
    <div className="centered" style={{ padding: "2rem 1rem" }}>
      <div className="card stack" style={{ width: "min(30rem, 100%)" }}>
        <div>
          <h1 className="card__title">Activer la double authentification</h1>
          <p className="card__lead">
            Elle est obligatoire pour le propriétaire et les administrateurs d'un compte : elle protège vos
            documents et votre crédit.
          </p>
        </div>
        {uri && (
          <>
            <div style={{ textAlign: "center" }}>
              <canvas ref={canvasRef} style={{ maxWidth: "100%" }} />
            </div>
            <p className="muted">
              Scannez ce code avec une application d'authentification (Google Authenticator, Microsoft
              Authenticator…). Sans caméra, saisissez la clé :
            </p>
            <div className="secret-once">
              <code>{secret}</code>
              <p style={{ margin: 0 }}>Conservez-la : sans elle et sans votre téléphone, vous perdez l'accès.</p>
            </div>
            <div className="field">
              <label className="field-label" htmlFor="activation">Code affiché par l'application</label>
              <input id="activation" className="code-input" value={code} inputMode="numeric"
                     autoComplete="one-time-code" maxLength={6} autoFocus
                     onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))} />
            </div>
          </>
        )}
        <Erreur erreur={erreur} />
        <div className="actions" style={{ marginTop: 0 }}>
          <button type="button" className="btn btn--primary" disabled={!uri || code.length !== 6 || envoi}
                  onClick={() => {
                    setEnvoi(true);
                    setErreur(null);
                    api.mfaConfirm(email, code).then(onActivee, setErreur).finally(() => setEnvoi(false));
                  }}>
            Activer
          </button>
          <button type="button" className="btn btn--link" onClick={onAnnuler}>Annuler</button>
        </div>
      </div>
    </div>
  );
}
